import { useEffect, useState } from "react";
import axios from "axios";
import { API, getAuthHeader } from "@/App";
import { toast } from "sonner";
import { Settings2, Save, Newspaper, Send, Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

// Step i = time from the previous available stage to this stage.
export const TURNAROUND_STEPS = [
  { key: "packing_list_at", limit: "order_to_packing", label: "Order → Packing List" },
  { key: "bulk_data_at", limit: "packing_to_bulk_data", label: "Packing List → Bulk Data" },
  { key: "pack_team_at", limit: "bulk_data_to_pack_team", label: "Bulk Data → Pack Team" },
  { key: "bulk_label_at", limit: "pack_team_to_label", label: "Pack Team → Labels" },
];
const ORDER = ["order_start_at", ...TURNAROUND_STEPS.map(s => s.key)];

export const hoursBetween = (a, b) => (a && b ? (new Date(b) - new Date(a)) / 3600000 : null);

export const fmtHours = (h) => {
  if (h == null || h < 0) return null;
  const m = Math.round(h * 60);
  if (m < 1) return "<1m";
  const d = Math.floor(m / 1440), hr = Math.floor((m % 1440) / 60), mm = m % 60;
  return d ? `${d}d ${hr}h` : hr ? `${hr}h ${mm}m` : `${mm}m`;
};

// Step durations for a flow row: { [stageKey]: hours }, plus total.
export const rowTurnaround = (row) => {
  const out = {};
  TURNAROUND_STEPS.forEach((s) => {
    const i = ORDER.indexOf(s.key);
    const prev = ORDER.slice(0, i).reverse().find(k => row[k]);
    if (row[s.key] && prev) {
      out[s.key] = hoursBetween(row[prev], row[s.key]);
      out[`${s.key}_limits`] = TURNAROUND_STEPS.slice(ORDER.indexOf(prev), i).map(x => x.limit);
    }
  });
  const stamps = ORDER.map(k => row[k]).filter(Boolean);
  const start = row.order_start_at || stamps.reduce((a, b) => (a < b ? a : b), stamps[0]);
  out.total = stamps.length > 1 ? hoursBetween(start, row.bulk_label_at || stamps.reduce((a, b) => (a > b ? a : b))) : null;
  out.complete = !!(row.order_start_at && row.bulk_label_at);
  return out;
};

// Limit for a step = sum of the limits of every stage it spans (skipped stages included).
export const stepLimitHours = (ta, key, limits) =>
  (ta[`${key}_limits`] || []).reduce((n, k) => n + (Number(limits?.[k]) || 0), 0) || null;

export const useTurnaroundLimits = () => {
  const [limits, setLimits] = useState(null);
  useEffect(() => {
    axios.get(`${API}/reports/turnaround-limits`, { headers: getAuthHeader() })
      .then(r => setLimits(r.data)).catch(() => setLimits({}));
  }, []);
  return [limits, setLimits];
};

const FIELDS = [...TURNAROUND_STEPS.map(s => [s.limit, s.label]), ["total", "Total (Order → Labels)"]];

export const TurnaroundLimitsEditor = ({ limits, onSaved }) => {
  const [open, setOpen] = useState(false);
  const [vals, setVals] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (limits) setVals(limits); }, [limits]);
  const save = async () => {
    setSaving(true);
    try {
      const r = await axios.put(`${API}/reports/turnaround-limits`, vals, { headers: getAuthHeader() });
      onSaved(r.data); setOpen(false); toast.success("Slow-step limits saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save limits"); }
    finally { setSaving(false); }
  };
  return (
    <div className="relative">
      <button onClick={() => setOpen(!open)} data-testid="turnaround-limits-btn"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700">
        <Settings2 className="w-3.5 h-3.5" /> Slow limits
      </button>
      {open && (
        <div className="absolute right-0 top-full mt-1 z-30 w-72 bg-white border border-slate-200 rounded-lg shadow-xl p-3 space-y-2" data-testid="turnaround-limits-panel">
          <div className="text-xs font-semibold text-slate-700">Mark red when a step takes longer than (hours)</div>
          {FIELDS.map(([k, label]) => (
            <label key={k} className="flex items-center justify-between gap-2 text-xs text-slate-600">
              {label}
              <input type="number" min="0.1" step="0.5" value={vals[k] ?? ""} data-testid={`limit-input-${k}`}
                onChange={e => setVals(v => ({ ...v, [k]: e.target.value }))}
                className="w-20 px-2 py-1 border border-slate-300 rounded text-right" />
            </label>
          ))}
          <button onClick={save} disabled={saving} data-testid="turnaround-limits-save"
            className="w-full inline-flex items-center justify-center gap-1 px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-60">
            <Save className="w-3.5 h-3.5" /> {saving ? "Saving..." : "Save limits"}
          </button>
        </div>
      )}
    </div>
  );
};

// Average per step + slow counts across the shown invoices.
export const TurnaroundSummary = ({ rows, limits }) => {
  const cells = [...TURNAROUND_STEPS.map(s => [s.key, s.limit, s.label]), ["total", "total", "Total"]].map(([k, lk, label]) => {
    const tas = rows.map(rowTurnaround).filter(t => t[k] != null && t[k] >= 0);
    const hs = tas.map(t => t[k]);
    const avg = hs.length ? hs.reduce((a, b) => a + b, 0) / hs.length : null;
    const lim = (t) => (k === "total" ? Number(limits?.total) : stepLimitHours(t, k, limits));
    const slow = tas.filter(t => lim(t) && t[k] > lim(t)).length;
    return { k, label, avg, slow };
  });
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="turnaround-summary">
      {cells.map(c => (
        <div key={c.k} className={`rounded-lg border px-3 py-2 ${c.slow ? "border-rose-200 bg-rose-50" : "border-slate-200 bg-slate-50"}`} data-testid={`turnaround-avg-${c.k}`}>
          <div className="text-[11px] text-slate-500">{c.label} · avg</div>
          <div className="text-base font-bold text-slate-800">{fmtHours(c.avg) || "—"}</div>
          <div className={`text-[11px] ${c.slow ? "text-rose-600 font-semibold" : "text-slate-400"}`}>{c.slow} slow</div>
        </div>
      ))}
    </div>
  );
};

// Admin: yesterday's slow invoices (same content the 11:55 AM IST bell digest sends).
export const TurnaroundDigestButton = () => {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState("");
  const [d, setD] = useState(null);
  useEffect(() => {
    if (!open) return;
    if (!date) { const y = new Date(Date.now() + 330 * 60000 - 86400000); setDate(y.toISOString().split("T")[0]); return; }
    setD(null);
    axios.get(`${API}/reports/turnaround-digest`, { headers: getAuthHeader(), params: { date } })
      .then(r => setD(r.data))
      .catch(e => { setD({ slow_invoices: [] }); toast.error(e.response?.data?.detail || "Could not load digest"); });
  }, [open, date]);
  const sendNow = async () => {
    try {
      const r = await axios.post(`${API}/reports/turnaround-digest/send-now`, {}, { headers: getAuthHeader() });
      toast.success(`Digest sent to ${r.data.sent_to} admin(s)`);
    } catch { toast.error("Could not send digest"); }
  };
  return (
    <>
      <button onClick={() => setOpen(true)} data-testid="turnaround-digest-btn"
        className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border border-slate-300 bg-white hover:bg-slate-50 text-slate-700 whitespace-nowrap">
        <Newspaper className="w-3.5 h-3.5" /> Yesterday's Digest
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="turnaround-digest-dialog">
          <DialogHeader><DialogTitle>Turnaround Digest</DialogTitle></DialogHeader>
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <input type="date" value={date} onChange={e => setDate(e.target.value)} data-testid="digest-date"
              className="px-2 py-1 border border-slate-300 rounded-lg text-sm" />
            {d?.date && <span className="text-slate-600" data-testid="digest-count"><b className={d.slow_count ? "text-rose-600" : "text-emerald-600"}>{d.slow_count}</b> slow of {d.invoices_checked} invoice(s)</span>}
            <button onClick={sendNow} data-testid="digest-send-now"
              className="ml-auto inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 hover:bg-blue-700 text-white">
              <Send className="w-3.5 h-3.5" /> Send to admins now
            </button>
          </div>
          {!d ? <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline" /></div>
            : d.slow_invoices.length === 0 ? <div className="p-6 text-center text-emerald-700 text-sm" data-testid="digest-empty">No slow invoices on this day.</div>
            : (
              <div className="space-y-2">
                {d.slow_invoices.map(r => (
                  <div key={r.invoice_no} className="rounded-lg border border-rose-200 bg-rose-50/50 p-3" data-testid={`digest-row-${r.invoice_no}`}>
                    <div className="flex items-center justify-between">
                      <span className="font-semibold text-slate-800">{r.invoice_no}</span>
                      <span className={`text-xs font-semibold ${r.total_slow ? "text-rose-600" : "text-slate-600"}`}>
                        Total {r.total || "—"}{!r.complete && " (in progress)"}{r.total_slow && ` · over ${d.limits.total}h`}
                      </span>
                    </div>
                    {r.steps.map(s => (
                      <div key={s.step} className="mt-1 text-xs text-slate-700">
                        <span className="font-medium text-rose-700">{s.step}</span> took {s.took} (limit {s.limit_h}h){s.by && <> · handled by <b>{s.by}</b></>}
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            )}
          <p className="text-[11px] text-slate-500">Sent automatically to every admin's bell at 11:55 AM IST for the previous day.</p>
        </DialogContent>
      </Dialog>
    </>
  );
};
